import React, { useState, useEffect, useRef } from 'react';
import { X, Send, AlertTriangle } from 'lucide-react';
import { usePreviewEmail, useSendEmail } from '../../../hooks/useReservationComms';
import { useToast } from '../../../contexts/ToastContext';

function SendPreviewModal({ reservationId, template, context, onClose }) {
  const previewMutation = usePreviewEmail(reservationId);
  const sendMutation = useSendEmail(reservationId);
  const toast = useToast();
  const [preview, setPreview] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);

  const recipientEmail = context?.customer?.email;
  const hasRecipient = !!recipientEmail;

  // Load preview on mount
  useEffect(() => {
    const loadPreview = async () => {
      try {
        const result = await previewMutation.mutateAsync({
          templateId: template.id,
          context,
        });
        setPreview(result.data);
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to load preview');
      }
    };

    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id]);

  // Focus trap
  useEffect(() => {
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSend = async () => {
    if (!hasRecipient) {
      toast.error('No recipient email available');
      return;
    }

    if (isSending) return;

    setIsSending(true);
    try {
      await sendMutation.mutateAsync({
        templateId: template.id,
        context,
      });
      toast.success('Email queued for delivery');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send email');
      setIsSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-panel send-preview-modal"
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
      >
        <div className="modal-header">
          <h2>Send {template.name}</h2>
          <button
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
            ref={firstFocusableRef}
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {!hasRecipient && (
            <div className="send-preview-warning">
              <AlertTriangle size={16} />
              <span>No recipient email available in context. Cannot send.</span>
            </div>
          )}

          <div className="send-preview-field">
            <label className="send-preview-label">To:</label>
            <div className="send-preview-value">
              {recipientEmail || <span className="send-preview-missing">No email</span>}
            </div>
          </div>

          {preview && (
            <>
              <div className="send-preview-field">
                <label className="send-preview-label">Subject:</label>
                <div className="send-preview-value">{preview.subject}</div>
              </div>

              <div className="send-preview-field">
                <label className="send-preview-label">Body Preview:</label>
                <div className="send-preview-iframe-container">
                  <iframe
                    srcDoc={preview.htmlBody}
                    className="send-preview-iframe"
                    title="Email Preview"
                    sandbox=""
                  />
                </div>
              </div>
            </>
          )}

          {previewMutation.isPending && (
            <div className="send-preview-loading">Loading preview...</div>
          )}

          {previewMutation.isError && (
            <div className="send-preview-error">
              Failed to load preview: {previewMutation.error?.response?.data?.error || 'Unknown error'}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={isSending}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSend}
            disabled={!hasRecipient || isSending || !preview}
          >
            {isSending ? (
              <>Sending...</>
            ) : (
              <>
                <Send size={16} />
                Send Now
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SendPreviewModal;
